import { FC, Suspense, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from 'react-relay';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Class } from '../../models/Class';
import {
  hideClass as hideClassMutation,
  showClass as showClassMutation,
} from '../../graphQLOperations/ClassCategories';
import { ClassCategoriesHideClassMutation } from '../../graphQLOperations/__generated__/ClassCategoriesHideClassMutation.graphql';
import { ClassCategoriesShowClassMutation } from '../../graphQLOperations/__generated__/ClassCategoriesShowClassMutation.graphql';
import HorizontalLoading from '../../assets/HorizontalLoading/HorizontalLoading';
import ClassesRostersContent from './ClassesRostersContent';
import ClassUsersRefetcher from './ClassUsersRefetcher';
import ClassesRosterClassFormDialog from './ClassesRosterDialog/ClassesRosterClassFormDialog';
import ClassManageStudentsDialog from './ClassesRosterWizard/ClassManageStudentsDialog';
import ClassManageCoTeachersDialog from './ClassesRosterWizard/ClassManageCoTeachersDialog';
import { ClassMenuAction } from './ClassesRosterCardMenu';

type RosterMode = 'students' | 'teachers';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  clearJustHiddenClass,
  noteClassHidden,
  selectJustHiddenClassName,
  setClassHidden,
} from '../../redux/reducers/hiddenClassesSlice';

interface Props {
  isAdmin: boolean;
}

const COLUMN_CLASSES = 'col-10 col-offset-1 lg:col-12 lg:col-offset-0 flex flex-column nc_content-frame';

// Marks that the teacher has already seen the "start with this class" pulse at least once, so
// generic entries (header menu, direct URL) only nudge them the very first time.
const PULSE_SEEN_KEY = 'classes_roster_pulse_seen';

const ClassesRostersBody: FC<Props> = ({ isAdmin }) => {
  const location = useLocation();
  // Whether to light up the first class card, captured once for this visit:
  //  - Arriving from the diagnostic widget's Review classes step always nudges.
  //  - Any other entry path nudges only the first time ever, then remembers via localStorage.
  const [highlightFirstClass] = useState<boolean>(() => {
    const cameFromReviewClasses = Boolean(
      (location.state as { highlightFirstClass?: boolean } | null)?.highlightFirstClass,
    );
    const alreadySeen = localStorage.getItem(PULSE_SEEN_KEY) === 'true';
    return cameFromReviewClasses || !alreadySeen;
  });

  // Remember that the nudge has been shown so future generic visits stay quiet.
  useEffect(() => {
    if (highlightFirstClass) localStorage.setItem(PULSE_SEEN_KEY, 'true');
  }, [highlightFirstClass]);
  const [classModal, setClassModal] = useState<{ display: boolean; isEdit: boolean }>({
    display: false,
    isEdit: false,
  });
  const [wizardVisible, setWizardVisible] = useState(false);
  // Created/edited this session, merged in optimistically so the grid reflects them before a refetch.
  const [createdClasses, setCreatedClasses] = useState<Class[]>([]);
  const [classList, setClassList] = useState<Class[]>([]);
  const [editingClass, setEditingClass] = useState<Class | null>(null);
  const [rosterModal, setRosterModal] = useState<{ cls: Class; mode: RosterMode } | null>(null);
  // Class just rostered: refetched fresh so its card's student count / co-teacher label update instantly.
  const [rosteredClass, setRosteredClass] = useState<Class | null>(null);
  const [hideCandidate, setHideCandidate] = useState<Class | null>(null);
  // Bumped to remount the content and force a fresh class-list fetch.
  const [refreshKey, setRefreshKey] = useState(0);
  const toast = useRef<Toast | null>(null);
  const dispatch = useAppDispatch();

  const justHiddenName = useAppSelector(selectJustHiddenClassName);
  useEffect(() => {
    if (!justHiddenName) return;
    toast.current?.show({ severity: 'success', summary: 'Class hidden', detail: `"${justHiddenName}" hidden` });
    dispatch(clearJustHiddenClass());
  }, [justHiddenName, dispatch]);

  const [commitHide, hiding] = useMutation<ClassCategoriesHideClassMutation>(hideClassMutation);
  const [commitShow] = useMutation<ClassCategoriesShowClassMutation>(showClassMutation);

  const openCreateClass = () => {
    setWizardVisible(true);
  };

  const handleClassSaved = (saved: Class) => {
    setCreatedClasses(prev => [...prev.filter(cls => cls.id !== saved.id), saved]);
    setRefreshKey(key => key + 1);
  };

  const handleMenuAction = (action: ClassMenuAction, cls: Class) => {
    if (action === 'edit') {
      setEditingClass(cls);
      setClassModal({ isEdit: true, display: true });
      return;
    }
    if (action === 'addStudents') {
      setRosterModal({ cls, mode: 'students' });
      return;
    }
    if (action === 'addCoTeacher') {
      setRosterModal({ cls, mode: 'teachers' });
      return;
    }
    if (action === 'hide') {
      setHideCandidate(cls);
    }
  };

  const confirmHide = () => {
    if (!hideCandidate) return;
    const classId = hideCandidate.id;
    commitHide({
      variables: { input: { classId } },
      onCompleted: response => {
        const error = response.hideClass?.errors?.[0];
        if (error && 'message' in error) {
          toast.current?.show({ severity: 'error', summary: 'Error', detail: error.message });
          return;
        }
        dispatch(setClassHidden({ classId, hidden: true }));
        dispatch(noteClassHidden(hideCandidate.name ?? ''));
        setHideCandidate(null);
      },
      onError: () => toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not hide the class.' }),
    });
  };

  const handleUnhide = (cls: Class) => {
    commitShow({
      variables: { input: { classId: cls.id } },
      onCompleted: () => {
        toast.current?.show({ severity: 'success', summary: 'Class restored', detail: `"${cls.name}" unhidden` });
        dispatch(setClassHidden({ classId: cls.id, hidden: false }));
      },
      onError: () =>
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not unhide the class.' }),
    });
  };

  // Refetch just this class so getClassCardData recomputes student count / co-teacher from fresh users.
  const handleRostered = (cls: Class) => setRosteredClass(cls);

  const handleRosterUsersFetched = (users: Class['users']) => {
    if (!rosteredClass) return;
    const merged = { ...rosteredClass, users };
    setCreatedClasses(prev => [...prev.filter(cls => cls.id !== merged.id), merged]);
    setRosteredClass(null);
  };

  const hideDialogFooter = (
    <div className="flex justify-content-end gap-md w-full">
      <Button label="Cancel" outlined rounded onClick={() => setHideCandidate(null)} disabled={hiding} />
      <Button label="Hide class" rounded onClick={confirmHide} loading={hiding} />
    </div>
  );

  return (
    <div className={COLUMN_CLASSES}>
      <Toast ref={toast} />

      <Suspense fallback={<HorizontalLoading />}>
        <ClassesRostersContent
          key={refreshKey}
          isAdmin={isAdmin}
          createdClasses={createdClasses}
          onCreateClass={openCreateClass}
          onMenuAction={handleMenuAction}
          onUnhide={handleUnhide}
          highlightFirstClass={highlightFirstClass}
          wizardVisible={wizardVisible}
          onHideWizard={() => setWizardVisible(false)}
          onClassCreated={handleClassSaved}
          toast={toast}
        />
      </Suspense>

      {classModal.display && (
        <ClassesRosterClassFormDialog
          classList={classList}
          setClassList={setClassList}
          isEdit={classModal.isEdit}
          visible={classModal.display}
          setVisible={setClassModal}
          initialEditValues={editingClass}
          toast={toast}
          setSelectedClass={handleClassSaved}
          entryPoint="class_card_menu"
        />
      )}

      {rosterModal && (
        <Suspense fallback={null}>
          {rosterModal.mode === 'students' ? (
            <ClassManageStudentsDialog
              visible
              onHide={() => setRosterModal(null)}
              selectedClass={rosterModal.cls}
              toast={toast}
              onSaved={() => handleRostered(rosterModal.cls)}
            />
          ) : (
            <ClassManageCoTeachersDialog
              visible
              onHide={() => setRosterModal(null)}
              selectedClass={rosterModal.cls}
              toast={toast}
              onSaved={() => handleRostered(rosterModal.cls)}
            />
          )}
        </Suspense>
      )}

      {rosteredClass && (
        <Suspense fallback={null}>
          <ClassUsersRefetcher classId={rosteredClass.id} onFetched={handleRosterUsersFetched} />
        </Suspense>
      )}

      <Dialog
        header={`Hide ${hideCandidate?.name ?? ''}?`}
        visible={Boolean(hideCandidate)}
        footer={hideDialogFooter}
        onHide={() => setHideCandidate(null)}
        className="nc_hide-dialog"
        style={{ width: '32rem' }}
        dismissableMask
        closable={false}
      >
        <p className="m-0">
          Hidden classes will no longer appear in your Class selection dropdowns.{' '}
          <b>You can un-hide them from the Hidden classes section on the class homepage.</b>
        </p>
      </Dialog>
    </div>
  );
};

export default ClassesRostersBody;
